import React from 'react';
import { AvTooltip, FeatureFlags } from '@fabric-ui/components';
import { MeasurementCategory } from '@fabric-ui/components/Table';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box, SxProps } from '@mui/material';
import { ChartTooltipItem, ChartTooltipTitle } from '../../../../../components/Widgets/ChartTooltip/ChartTooltipItem';
import { ChartClickInteraction } from '../../../../../components/Widgets/types';
import { WidgetTooltipContent } from '../../../../../components/Widgets/WidgetTooltip/WidgetTooltip';
import { useAvContext } from '../../../../../context/AvContextProvider';
import { isPercentageMetric } from '../../../../../utils/dashboardDataUtils';
import { getPercent } from '../../../../../utils/utils';
import { useGetDisplayName } from '../../../hooks/useGetDisplayName';
import { useGetFieldType } from '../../../hooks/useGetFieldType';
import { tileTitleSX } from '../../../types';

interface Props {
  value: string | number;
  metricName: string;
  clickInteractions?: ChartClickInteraction[];
  titleSx?: SxProps;
  projName: string;
}

const ClickableTitle = ({ value, metricName, titleSx = tileTitleSX, clickInteractions, projName }: Props) => {
  const {
    featureFlags,
    measurements: { asObject },
  } = useAvContext();
  const isEnabledClickthrough = clickInteractions?.some(({ isEnabledForCell }) => isEnabledForCell({ dataKey: metricName }));
  const getMetricType = useGetFieldType(projName);
  const metricType = getMetricType({ entityFieldKey: metricName });
  const numericValue =
    isPercentageMetric(metricName) || metricType === 'percentage' || asObject[metricName].category === MeasurementCategory.PERCENTAGE
      ? getPercent(value)
      : abbreviateNumber(value);
  const getDisplayName = useGetDisplayName(projName);
  return (
    <Box
      sx={{
        px: 1,
        width: 'fit-content',
        '&:hover': clickInteractions
          ? {
              background: theme => theme.palette.colors.neutrals[200],
              borderRadius: '10px',
            }
          : {},
      }}
      className="draggableCancel">
      <AvTooltip
        muiProps={{
          placement: 'right',
          componentsProps: {
            popper: { sx: { '& .MuiTooltip-tooltip': { p: 0 } } },
          },
        }}
        title={
          <WidgetTooltipContent
            payload={[{ dataKey: metricName, payload: [{ payload: { [metricName]: value }, dataKey: metricName }] }]}
            widgetData={[]}
            title={
              <div>
                <ChartTooltipItem title={getDisplayName(metricName)} value={numericValue} />
                <ChartTooltipTitle fontWeight="normal">Latest Value at the end of selected period</ChartTooltipTitle>
              </div>
            }
            chartTooltipProps={{ maxWidth: 'auto' }}
            interactions={clickInteractions}
          />
        }>
        <Box
          sx={{
            fontSize: 32,
            color: 'var(--color-content-base-primary)',
            fontWeight: 500,
            cursor: isEnabledClickthrough ? 'pointer' : 'default',
            ...titleSx,
          }}
          onClick={isEnabledClickthrough ? clickInteractions![0].onClick : undefined}>
          {featureFlags[FeatureFlags.TileWidgetSupportsText] && typeof value !== 'number' ? value : numericValue}
        </Box>
      </AvTooltip>
    </Box>
  );
};

export default ClickableTitle;
